import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type BannerDataFragment } from '../../../api/fragments/BannerData';
import {
    DeleteBannerImageDocument,
    type DeleteBannerImageMutation,
    type DeleteBannerImageMutationVariables,
} from '../../../api/mutations/deleteBannerImage';
import {
    UploadBannerImageDocument,
    type UploadBannerImageMutation,
    type UploadBannerImageMutationVariables,
} from '../../../api/mutations/uploadBannerImage';
import { BannerAsset } from '../../../api/types';
import { type BannerFormikValue } from './BannerDetailsInnerPage';

const useUploadBannerImage = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (bannerId: string, values: BannerFormikValue, initialValues?: BannerDataFragment) => {
            const { bannerImage, mobileBannerImage } = values;
            const uploads = [];

            if (bannerImage instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBannerImageMutation, UploadBannerImageMutationVariables>({
                        mutation: UploadBannerImageDocument,
                        variables: {
                            id: bannerId,
                            uploads: bannerImage,
                            bannerAsset: BannerAsset.ConfiguratorBanner,
                        },
                    })
                );
            } else if (bannerImage === null && isObject(initialValues?.bannerImage)) {
                uploads.push(
                    apolloClient.mutate<DeleteBannerImageMutation, DeleteBannerImageMutationVariables>({
                        mutation: DeleteBannerImageDocument,
                        variables: {
                            id: bannerId,
                            imageId: bannerImage.id,
                            bannerAsset: BannerAsset.ConfiguratorBanner,
                        },
                    })
                );
            }

            if (mobileBannerImage instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBannerImageMutation, UploadBannerImageMutationVariables>({
                        mutation: UploadBannerImageDocument,
                        variables: {
                            id: bannerId,
                            uploads: mobileBannerImage,
                            bannerAsset: BannerAsset.ConfiguratorMobileBanner,
                        },
                    })
                );
            } else if (mobileBannerImage === null && isObject(initialValues?.mobileBannerImage)) {
                uploads.push(
                    apolloClient.mutate<DeleteBannerImageMutation, DeleteBannerImageMutationVariables>({
                        mutation: DeleteBannerImageDocument,
                        variables: {
                            id: bannerId,
                            imageId: mobileBannerImage.id,
                            bannerAsset: BannerAsset.ConfiguratorMobileBanner,
                        },
                    })
                );
            }

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useUploadBannerImage;
